import React, { useState, useEffect } from "react";
import "./AddClassFeedback.css";
import { MoonLoader, PulseLoader } from "react-spinners";
import { Formik } from "formik";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import Rating from "@mui/material/Rating";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import TextArea from "../../../Components/Atoms/Inputs/TextArea/TextArea";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCookies } from "react-cookie";
import { generateStudentFeedbackWithAi } from "../../../Hooks/aiService";

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    color: theme.palette.action.disabled,
  },
}));

const customIcons = {
  1: {
    icon: (
      <SentimentVeryDissatisfiedIcon color="error" className="reportIcon" />
    ),
    label: "Poor",
  },
  2: {
    icon: <SentimentDissatisfiedIcon color="error" className="reportIcon" />,
    label: "Fair",
  },
  3: {
    icon: <SentimentSatisfiedIcon color="warning" className="reportIcon" />,
    label: "Good",
  },
  4: {
    icon: <SentimentSatisfiedAltIcon color="success" className="reportIcon" />,
    label: "Very Good",
  },
  5: {
    icon: <SentimentVerySatisfiedIcon color="success" className="reportIcon" />,
    label: "Excellent",
  },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return (
    <span
      {...other}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <span>{customIcons[value].icon}</span>
      <span className="ratingLabel">{customIcons[value].label}</span>
    </span>
  );
}

const FeedbackSchema = Yup.object().shape({
  description: Yup.string().required("Feedback Summary is required !!"),
});

function AddClassFeedback({
  student,
  Class,
  attributes,
  report,
  reportLoading,
  loading,
  studentLoading,
  setGoSave,
  classId,
  studentId,
  addLoading,
  editLoading,
  setNewObject,
  Edit,
  setGoEdit,
}) {
  const navigate = useNavigate();
  const [cookies] = useCookies(["TOKEN"]);
  const [attrs, setAttr] = useState([]);
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);

  useEffect(() => {
    if (attributes?.courseAttributes?.length && !Edit) {
      setAttr(
        attributes?.courseAttributes?.map((att) => ({
          id: att?.id,
          name: att?.attribute,
          value: 0,
          number: att?.number,
        }))
      );
    }
  }, [attributes]);

  useEffect(() => {
    if (report && !reportLoading && Edit) {
      setAttr(
        report?.attributes?.map((att, index) => ({
          id: att?.courseAttribute?.id,
          name: att?.courseAttribute?.attribute,
          value: att?.value,
          number: index + 1,
        }))
      );
    }
  }, [report]);

  // AI Note Generation Function (via secure backend proxy)
  const generateAINote = async (setFieldValue) => {
    setIsGeneratingNote(true);

    try {
      const validRatings = attrs
        .filter((attr) => attr.value > 0)
        .map((attr) => ({
          attribute: attr.name,
          rating: Number(attr.value),
        }));

      if (validRatings.length === 0) {
        toast.warn("Please select at least one rating first.");
        return;
      }

      const res = await generateStudentFeedbackWithAi({
        studentName: student?.name,
        courseName: Class?.course?.title || Class?.name,
        ratings: validRatings,
        token: cookies.TOKEN,
      });

      if (res?.data?.text) {
        setFieldValue("description", res.data.text);
        toast.success("Feedback note generated successfully!");
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate feedback with AI";
      toast.error(errorMsg);
    } finally {
      setIsGeneratingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="mainContainer">
        <div className="loadingContainer">
          <MoonLoader color="#012369" size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="mainContainer">
      <div className="contentSection modernFormWrapper">
        {/* Header */}
        <div className="courseHeader">
          <div className="titleIcon">
            <AssessmentIcon />
          </div>
          <div className="courseBasicInfo">
            <h1 className="courseName">{student?.name} Report</h1>
          </div>
        </div>

        {(!loading && !Edit) || (!loading && report && Edit) ? (
          <Formik
            initialValues={{
              description: Edit ? report?.description : "",
            }}
            validationSchema={FeedbackSchema}
            onSubmit={(values) => {
              setNewObject({
                ...values,
                studentId: Number(studentId),
                attributes: attrs?.map((item) => ({
                  courseAttributeId: item?.id,
                  value: Number(item?.value),
                })),
              });
              Edit ? setGoEdit(true) : setGoSave(true);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form autoComplete="off" onSubmit={handleSubmit}>
                {/* Attributes Section */}
                <div className="lessonsHeader">
                  <h2 className="sectionTitle">Performance Attributes</h2>
                </div>

                <div className="attributesContainer">
                  {(attrs?.length && report && Edit) || !Edit
                    ? attrs
                        ?.sort((a, b) => a?.number - b?.number)
                        ?.map((item, index) => (
                          <div key={index} className="attributeCardFeedback">
                            <div className="attributeHeader">
                              <h3 className="attributeNameFeedback">
                                {item?.name}
                              </h3>
                            </div>
                            <div className="ratingContainer">
                              <StyledRating
                                name={`rating-${index}`}
                                IconContainerComponent={IconContainer}
                                highlightSelectedOnly
                                value={item?.value}
                                onChange={(e) => {
                                  setAttr([
                                    ...attrs?.filter(
                                      (attrib) => attrib?.id != item?.id
                                    ),
                                    {
                                      ...attrs?.find(
                                        (attrib) => attrib?.id == item?.id
                                      ),
                                      value: e.target.value,
                                    },
                                  ]);
                                }}
                              />
                            </div>
                          </div>
                        ))
                    : ""}
                </div>

                {/* Notes Section */}
                <div className="notesSection">
                  <div className="lessonsHeader">
                    <h2 className="sectionTitle">Notes:</h2>
                    <button
                      type="button"
                      className="actionButton secondary generateNoteButton"
                      onClick={() => generateAINote(setFieldValue)}
                      disabled={
                        isGeneratingNote ||
                        attrs.every((attr) => attr.value === 0)
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px 20px",
                        fontSize: "14px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        border: "1px solid #012369",
                        backgroundColor: "transparent",
                        color: "#012369",
                        cursor: attrs.every((attr) => attr.value === 0)
                          ? "not-allowed"
                          : "pointer",
                        opacity: attrs.every((attr) => attr.value === 0)
                          ? 0.5
                          : 1,
                      }}
                    >
                      <AutoFixHighIcon fontSize="small" />
                      {isGeneratingNote ? (
                        <>
                          <PulseLoader color="#012369" size={6} />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <span>Generate Note</span>
                      )}
                    </button>
                  </div>
                  <TextArea
                    style={{ marginBottom: "10px" }}
                    classes="textAreaInput"
                    label={"Notes"}
                    name={"description"}
                    newValue={values.description}
                    change={handleChange}
                    blur={handleBlur}
                  />
                  {errors.description &&
                    touched.description &&
                    errors.description && (
                      <p className="error">
                        {errors.description &&
                          touched.description &&
                          errors.description}
                      </p>
                    )}
                </div>

                {/* Submit Button */}
                <div className="submitContainer">
                  <button
                    className="actionButton primary addButton"
                    type="submit"
                    disabled={Edit ? editLoading : addLoading}
                  >
                    {Edit ? (
                      editLoading ? (
                        <PulseLoader color="white" size={8} />
                      ) : (
                        "Update Report"
                      )
                    ) : addLoading ? (
                      <PulseLoader color="white" size={8} />
                    ) : (
                      "Submit Report"
                    )}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        ) : (
          <div className="loadingContainer">
            <MoonLoader color="#012369" size={40} />
          </div>
        )}
      </div>
    </div>
  );
}

export default AddClassFeedback;
